/* =========================================================
   Affiche ÉQUITATION « Le parcours » — plan, profil, saut isolé.
   API : SNEqParcours.render(data, opts) -> string SVG
     data : { mode, titre, kicker, lieu, date, cavalier, cheval,
              penalites, temps, hauteur,
              mode 'plan'   → nb (nombre d'obstacles, 6 à 16)
              mode 'profil' → o1..o14 (« 1,10 »), fautes (« 7, 11 »)
              mode 'saut'   → hauteur, largeur, type, resultat }
     opts : { palette }

   Le plan du parcours est tiré d'un générateur DÉTERMINISTE amorcé par les
   données saisies : même saisie → même tracé, à l'écran comme à l'impression.
   C'est une représentation stylisée du parcours, pas un relevé officiel.

   Auto-suffisant (aucun fetch, aucune image) → re-render serveur direct.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    craie:    { label:'Craie',    bg:'#F7F4ED', ink:'#0B2D5B', muted:'#8B94A3', accent:'#FF6B2C', line:'#E3DDD0', card:'#FFFFFF', sable:'#E4D5B7', deep:'#D8C7A4' },
    sable:    { label:'Sable',    bg:'#EDE3D2', ink:'#2C3A2E', muted:'#8A8676', accent:'#C4622D', line:'#DDD0B9', card:'#F7F1E6', sable:'#DCCFB6', deep:'#CDBE9E' },
    carriere: { label:'Carrière', bg:'#12362B', ink:'#F4F1E8', muted:'#8FB3A5', accent:'#FF6B2C', line:'#1C5544', card:'#164536', sable:'#C9B489', deep:'#0B271F' },
    nuit:     { label:'Nuit',     bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F', sable:'#C9B489', deep:'#082444' },
  };
  const ROUGE = '#C4372B';

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const num = s => { const m = String(s == null ? '' : s).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  function stats(p, items, y, x0) {
    x0 = x0 || 54;
    const it = items.filter(x => String(x[0] || '').trim());
    if (!it.length) return '';
    const w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }

  /* Générateur déterministe : l'aperçu et le fichier d'impression doivent
     produire exactement le même parcours. */
  function graine(s) { let h = 2166136261; s = String(s); for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
  function tirage(seed) { let s = (seed || 1) >>> 0; return function () { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; }; }

  /** Obstacle vu de dessus : la barre et ses deux chandeliers. */
  function obstaclePlan(cx, cy, larg, angle, p, num) {
    const c = Math.cos(angle), s = Math.sin(angle), h = larg / 2;
    const x1 = cx - c*h, y1 = cy - s*h, x2 = cx + c*h, y2 = cy + s*h;
    return `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${p.ink}" stroke-width="5" stroke-linecap="round"/>`
      + `<circle cx="${x1.toFixed(1)}" cy="${y1.toFixed(1)}" r="4.5" fill="${p.ink}"/><circle cx="${x2.toFixed(1)}" cy="${y2.toFixed(1)}" r="4.5" fill="${p.ink}"/>`
      + T(cx - s*22, cy + c*22 + 5, 15, p.accent, String(num), { f:'Oswald', w:700 });
  }

  /** Obstacle vu de profil. `tombee` fait basculer la barre du haut au sol :
      une barre écrite en rouge ne se lit pas, une barre par terre si. */
  function obstacleProfil(cx, sol, larg, h, p, oxer, tombee) {
    const dx = larg / 2, ch = Math.max(4, larg * 0.030);
    let g = '';
    const poteau = x => `<rect x="${(x-ch/2).toFixed(1)}" y="${(sol-h-16).toFixed(1)}" width="${ch.toFixed(1)}" height="${(h+16).toFixed(1)}" rx="2" fill="${p.ink}" opacity="0.85"/>`;
    const barre = (y, w, col, op) => `<rect x="${(cx - w/2).toFixed(1)}" y="${(y-4).toFixed(1)}" width="${w.toFixed(1)}" height="8" rx="4" fill="${col}" opacity="${op}"/>`;
    g += poteau(cx - dx) + poteau(cx + dx);
    if (oxer) g += poteau(cx - dx*0.55) + poteau(cx + dx*0.55);
    /* UNE seule barre basse : avec deux, l'obstacle se lit « échelle ». */
    g += barre(sol - h * 0.42, dx*2, p.ink, 0.45);
    /* barre supérieure : en place, ou tombée au pied de l'obstacle */
    if (tombee) {
      g += `<g transform="rotate(-9 ${(cx - dx*0.7).toFixed(1)} ${(sol - 8).toFixed(1)})">${barre(sol - 8, dx*2, ROUGE, 1)}</g>`;
      g += `<line x1="${(cx-dx).toFixed(1)}" y1="${(sol-h).toFixed(1)}" x2="${(cx+dx).toFixed(1)}" y2="${(sol-h).toFixed(1)}" stroke="${ROUGE}" stroke-width="1.6" stroke-dasharray="5 5" opacity="0.55"/>`;
    } else g += barre(sol - h, dx*2, p.accent, 1);
    return g;
  }

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.craie;
    const mode = String(data.mode || 'plan').toLowerCase();
    const cx = PW / 2;

    const DEF = { plan: 'LE PARCOURS', profil: 'MON PARCOURS', saut: 'LE SAUT' };
    const titre = up(data.titre) || DEF[mode] || 'LE PARCOURS';
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || 'CONCOURS DE SAUT D’OBSTACLES';
    const sous = [up(data.lieu), up(data.date)].filter(Boolean).join(' · ');

    let corps = '', barre = '';

    if (mode === 'plan') {
      const n = Math.max(4, Math.min(16, Math.round(num(data.nb) || 10)));
      const aw = 420, ah = 500, x = cx - aw/2, y = 600 - ah/2;
      const r = tirage(graine([data.cavalier, data.cheval, data.date, n].join('|')));
      corps = `<rect x="${x}" y="${y}" width="${aw}" height="${ah}" rx="6" fill="${p.sable}"/>`
        + `<rect x="${x}" y="${y}" width="${aw}" height="${ah}" rx="6" fill="none" stroke="${p.line}" stroke-width="3"/>`;
      /* Les obstacles suivent une boucle qui balaie la carrière : un parcours
         réel ne repasse pas deux fois au même endroit. */
      const pts = [], angles = [];
      for (let i = 0; i < n; i++) {
        const t = i / n, boucle = t * Math.PI * 2 - Math.PI / 2;
        const rx = 0.34 + r() * 0.10, ry = 0.34 + r() * 0.10;
        pts.push([x + aw * (0.5 + Math.cos(boucle) * rx), y + ah * (0.5 + Math.sin(boucle) * ry)]);
        angles.push(boucle + Math.PI / 2 + (r() - 0.5) * 0.8);
      }
      let d = `M ${(x + aw*0.5).toFixed(1)} ${(y + ah*0.98).toFixed(1)}`;
      pts.forEach((c, i) => {
        const prev = i ? pts[i-1] : [x + aw*0.5, y + ah*0.98];
        const mx = (prev[0]+c[0])/2 + (i % 2 ? 38 : -38), my = (prev[1]+c[1])/2;
        d += ` Q ${mx.toFixed(1)} ${my.toFixed(1)} ${c[0].toFixed(1)} ${c[1].toFixed(1)}`;
      });
      corps += `<path d="${d}" fill="none" stroke="${p.accent}" stroke-width="2.6" stroke-dasharray="8 7" opacity="0.85"/>`;
      pts.forEach((c, i) => { corps += obstaclePlan(c[0], c[1], 56, angles[i], p, i + 1); });
      barre = stats(p, [[data.penalites, 'Pénalités'], [data.temps, 'Temps'], [data.hauteur, 'Hauteur']], 926);

    } else if (mode === 'profil') {
      const haut = [];
      for (let i = 1; i <= 14; i++) { const v = num(data['o' + i]); if (v != null) haut.push(v); }
      const fautes = new Set(String(data.fautes || '').split(/[^\d]+/).filter(Boolean).map(Number));
      if (haut.length) {
        const x0 = 78, x1 = PW - 78, sol = 762, pas = (x1 - x0) / haut.length;
        const mn = Math.min.apply(null, haut), mx = Math.max.apply(null, haut);
        corps = `<line x1="${x0-10}" y1="${sol}" x2="${x1+10}" y2="${sol}" stroke="${p.ink}" stroke-width="2.5"/>`;
        haut.forEach((h, i) => {
          /* Écart resserré : sur douze obstacles serrés, une amplitude de
             hauteur trop forte donne des piquets plutôt que des obstacles. */
          const ccx = x0 + pas*i + pas/2;
          const hp = 92 + (mx > mn ? (h - mn) / (mx - mn) : 0.5) * 96;
          const tombee = fautes.has(i + 1);
          corps += obstacleProfil(ccx, sol, Math.min(46, pas * 0.70), hp, p, false, tombee)
            + T(ccx, sol + 26, 13, p.muted, String(i+1), { f:'Oswald', w:700 })
            + T(ccx, sol - hp - 24, 13, tombee ? ROUGE : p.ink, h.toFixed(2).replace('.', ','), { w:700 });
        });
      } else corps = T(cx, 600, 21, p.muted, 'SAISISSEZ LA HAUTEUR D AU MOINS UN OBSTACLE', { w:600, ls:2 });
      barre = stats(p, [[data.penalites, 'Pénalités'], [data.temps, 'Temps'], [String(haut.length || ''), 'Obstacles']], 900);

    } else { /* saut */
      corps = obstacleProfil(cx, 700, 230, 250, p, true, false)
        + `<text x="${cx}" y="392" text-anchor="middle" font-family="Oswald" font-weight="700" fill="${p.ink}"><tspan font-size="128">${esc(up(data.hauteur) || '—')}</tspan></text>`
        + T(cx, 434, 15, p.muted, 'HAUTEUR FRANCHIE', { w:600, ls:5 });
      barre = stats(p, [[data.type, 'Type'], [data.penalites, 'Pénalités'], [data.largeur, 'Largeur']], 900);
    }

    const pied = [esc(up(data.cavalier)), esc(up(data.cheval))].filter(Boolean).join(' &amp; ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${corps}
${barre}
${pied ? T(cx, 1000, fit(pied, 690, 19, 0.60, 1, 12), p.ink, pied, { f:'Oswald', w:700, ls:1 }) : ''}
</svg>`;
  }

  window.SNEqParcours = { render, PALETTES };
})();
